import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";

const empty = {
  image_url: "",
  kicker: "",
  title: "",
  accent: "",
  tail: "",
  sort_order: 0,
  is_active: true,
};

export default function HeroSlidesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/hero-slides");
    setItems(r.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.image_url) return toast.error("An image is required");
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) };
      if (editingId) await api.put(`/hero-slides/${editingId}`, payload);
      else await api.post("/hero-slides", payload);
      toast.success(editingId ? "Slide updated" : "Slide added");
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };

  const edit = (s) => {
    setForm({
      image_url: s.image_url,
      kicker: s.kicker || "",
      title: s.title || "",
      accent: s.accent || "",
      tail: s.tail || "",
      sort_order: s.sort_order || 0,
      is_active: s.is_active ?? true,
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    await api.delete(`/hero-slides/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (s) => {
    await api.put(`/hero-slides/${s.id}`, { ...s, is_active: !s.is_active });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-xl font-semibold text-charcoal">Homepage carousel</h2>
          <p className="text-sm text-charcoal/60">{items.length} slides · active ones show on the storefront</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-slide-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>
              <Plus className="w-4 h-4 mr-1.5" /> New slide
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit slide" : "Add slide"}</DialogTitle>
              <DialogDescription>Wide landscape images work best. Only active slides appear on the storefront.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Slide image</Label>
                <label
                  className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-black/15 rounded-md p-6 bg-stone/20 hover:bg-stone/40 hover:border-forest/40 transition-colors cursor-pointer"
                  data-testid="slide-image-dropzone"
                >
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="slide-image-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
                        setForm((prev) => ({ ...prev, image_url: url }));
                        toast.success("Image uploaded");
                      } catch (err) {
                        toast.error(errMsg(err, "Upload failed"));
                      }
                    }}
                  />
                  {form.image_url ? (
                    <div className="w-full flex items-center gap-4">
                      <img src={form.image_url} alt="" className="w-28 h-20 rounded object-cover border border-black/10 shrink-0" />
                      <div className="flex-1 text-left">
                        <div className="text-sm text-charcoal font-medium">Image ready</div>
                        <div className="text-xs text-charcoal/60 truncate max-w-xs">{form.image_url}</div>
                        <div className="mt-2 flex gap-2">
                          <span className="text-xs text-forest underline">Replace</span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForm({ ...form, image_url: "" }); }}
                            className="text-xs text-terracotta hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto text-forest/60" />
                      <div className="mt-2 text-sm text-charcoal font-medium">Click to upload a slide image</div>
                      <div className="text-xs text-charcoal/50 mt-0.5">Wide landscape works best · up to 5 MB</div>
                    </div>
                  )}
                </label>
                <details className="text-xs mt-2">
                  <summary className="cursor-pointer text-charcoal/60 hover:text-forest">or paste an image URL</summary>
                  <Input
                    placeholder="https://..."
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="mt-2"
                    data-testid="slide-image-url"
                  />
                </details>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kicker (small label)</Label>
                  <Input value={form.kicker} onChange={(e) => setForm({ ...form, kicker: e.target.value })} placeholder="Weekend Deals" data-testid="slide-kicker" />
                </div>
                <div>
                  <Label>Sort order</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Title (before accent)</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The season," data-testid="slide-title" />
              </div>
              <div>
                <Label>Accent (highlighted word)</Label>
                <Input value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} placeholder="handpicked" data-testid="slide-accent" />
              </div>
              <div>
                <Label>Tail (after accent)</Label>
                <Input value={form.tail} onChange={(e) => setForm({ ...form, tail: e.target.value })} placeholder="delivered by lunchtime." data-testid="slide-tail" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="slide-active" />
                <Label className="mb-0">Show on storefront</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-slide-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Image</TableHead>
              <TableHead>Kicker · Copy</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-charcoal/50 py-8">No slides yet</TableCell></TableRow>
            )}
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`slide-row-${s.id}`}>
                <TableCell>
                  {s.image_url ? (
                    <img src={s.image_url} alt="" className="w-20 h-12 rounded object-cover" />
                  ) : (
                    <div className="w-20 h-12 rounded bg-stone flex items-center justify-center">
                      <ImageOff className="w-4 h-4 text-charcoal/40" />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-xs uppercase tracking-widest text-moss/80">{s.kicker || "—"}</div>
                  <div className="text-sm text-charcoal">
                    {s.title} <span className="text-terracotta font-medium">{s.accent}</span> {s.tail && `— ${s.tail}`}
                  </div>
                </TableCell>
                <TableCell>{s.sort_order}</TableCell>
                <TableCell><Switch checked={!!s.is_active} onCheckedChange={() => toggle(s)} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => edit(s)} data-testid={`edit-slide-${s.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`delete-slide-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-terracotta" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
